import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { constSelector, useRecoilValue } from 'recoil';
import { Box, Button, DialogActions, DialogContent, Grid, MenuItem, Table, TextField } from '@mui/material';
import DropCustom from 'components/dropZone/DropCustom';
import { ModalActionProps } from 'models/AutoModals';
import { CompanyType } from 'models/CompanyModel';
import { CarModel } from 'models/StockModel';
import companyList from 'recoil/atoms/companyList';
import auth from 'recoil/atoms/auth';
import Api from 'utils/api';
import { replaceItemAtIndex } from 'utils/common';


type UploadBatchPdfMultiPageDialogProps = {
} & ModalActionProps<{ companyId: number, data: any, file: File }>

const UploadBatchPdfMultiPageDialog = ({onFinish}: UploadBatchPdfMultiPageDialogProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const [file, setFile] = useState<File>();
    const [preview, setPreview] = useState<string>();
    const [data, setData] = useState<Pick<CarModel, 'make' | 'model' | 'chassisNumber'>[]>([]);
    const [options, setOptions] = useState<Pick<CarModel, 'make' | 'model' | 'chassisNumber'>[]>([]);
    const [optionsFor, setOptionsFor] = useState<number|undefined>();
    const companies = useRecoilValue(my.type === CompanyType.Shipper ? constSelector([]) : companyList(CompanyType.Shipper));

    const processFile = (files: File[]) => {
        const file = files[0];
        setFile(file);
        setPreview(URL.createObjectURL(file) + '#navpanes=0&scrollbar=0&pagemode=none');

        Api.util
            .pdfPageCount(file)
            .then(res => setData(
                [...Array(res.data.count).keys()]
                    .map(() => ({make: '', model: '', chassisNumber: ''}))
            ))
    }

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const companyId = my.type === CompanyType.Shipper ?
            my.company.id :
            Number(new FormData(event.currentTarget).get('companyId') as string);

        file && onFinish({companyId, data, file});
    };

    const changeChassis = (i: number, value: string) => {
        if (value.length > 6 && options.length > 1) { // possible selected from options
            const option = options.find(opt => opt.chassisNumber === value);

            setData(old => replaceItemAtIndex(old, i, option || {...old[i], chassisNumber: value}));
            setOptions([]);
            setOptionsFor(undefined);
        } else { // typed by hand
            setData(old => replaceItemAtIndex(old, i, {...old[i], chassisNumber: value}));
        }

        if (value.length > 3 && !options.length) { // full chassis is pasted or typed and no options
            if (value.length < data[i].chassisNumber.length)
                return; // Don't search on delete

            Api.util.searchCar(value)
                .then(res => {
                    if (res.data.length === 1) { // found only one car
                        setData(old => replaceItemAtIndex(old, i, res.data[0]))
                    } else { // add options to select from
                        setOptions(res.data);
                        setOptionsFor(i)
                    }
                });
        }
    }

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                {!file && (
                    <DropCustom
                        title={t('batch_upload_export_certificate_multi_page')}
                        options={{
                            onDropAccepted: processFile,
                            accept: {
                                'application/pdf': ['.pdf'],
                            },
                            multiple: false,
                        }}
                    />
                )}

                {file && (
                    <Grid container spacing={2}>
                        <Grid item sm={8}>
                            <embed
                                src={preview}
                                type="application/pdf"
                                height="700px"
                                width="100%"
                            />
                        </Grid>
                        <Grid item sm={4}>
                            {companies.length > 0 &&
                                <TextField
                                    id="companyId"
                                    name="companyId"
                                    label={t('select_shipper')}
                                    fullWidth
                                    required
                                    select
                                    defaultValue=""
                                >
                                    {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                </TextField>
                            }

                            <Table>
                                <thead>
                                <tr>
                                    <th></th>
                                    <th>{t('make')}</th>
                                    <th>{t('model')}</th>
                                    <th>{t('chassis')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {data.map((item, i) => (
                                    <tr>
                                        <td>{i + 1}</td>
                                        <td>
                                            <TextField
                                                fullWidth
                                                required
                                                value={item.make}
                                                onChange={e => setData(old => replaceItemAtIndex(old, i, {
                                                    ...old[i],
                                                    make: e.target.value
                                                }))}
                                            />
                                        </td>
                                        <td>
                                            <TextField
                                                fullWidth
                                                required
                                                value={item.model}
                                                onChange={e => setData(old => replaceItemAtIndex(old, i, {
                                                    ...old[i],
                                                    model: e.target.value
                                                }))}
                                            />
                                        </td>
                                        <td>
                                            <TextField
                                                fullWidth
                                                required
                                                value={item.chassisNumber}
                                                onChange={e => changeChassis(i, e.target.value)}
                                                inputProps={optionsFor === i ? {list: 'car-options'} : undefined}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Grid>
                    </Grid>
                )}

                {options.length > 1 && (
                    <datalist id="car-options">
                        {options.map(opt => (<option value={opt.chassisNumber}/>))}
                    </datalist>
                )}
            </DialogContent>

            {file && (
                <DialogActions>
                    <Button type="submit">{t('upload')}</Button>
                </DialogActions>
            )}
        </Box>
    );
};

export default UploadBatchPdfMultiPageDialog;
